import React, { useState } from 'react';
import { X, Loader2, Sparkles, RefreshCw, Check } from 'lucide-react';
import { apiClient } from '../services/api';
import { useBodyScrollLock } from '../hooks/useBodyScrollLock';
import { CardDetail } from './CardDetail';
import { highlightWordsWithLinks, WordWithCardId } from '../utils/highlightWords';

interface ReviseStoryModalProps {
  storyId: number;
  storyText: string;
  onClose: () => void;
  onReplace?: (revisedText: string, wordsUsed: WordWithCardId[]) => Promise<void>;
}

type VocabularySource = 'free' | 'library';


export function ReviseStoryModal({ storyId, storyText, onClose, onReplace }: ReviseStoryModalProps) {
  useBodyScrollLock();
  const [prompt, setPrompt] = useState('');
  const [followUpPrompt, setFollowUpPrompt] = useState('');
  const [vocabularySource, setVocabularySource] = useState<VocabularySource>('free');
  const [revised, setRevised] = useState<string | null>(null);
  const [wordCount, setWordCount] = useState<number | null>(null);
  const [wordsUsed, setWordsUsed] = useState<WordWithCardId[]>([]);
  const [openCardId, setOpenCardId] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [replacing, setReplacing] = useState(false);
  const [replaced, setReplaced] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleRevise = async () => {
    const activePrompt = revised ? followUpPrompt : prompt;
    setLoading(true);
    setError(null);
    setReplaced(false);
    try {
      const result = await apiClient.reviseStory(storyId, {
        ...(activePrompt ? { prompt: activePrompt } : {}),
        vocabulary_source: vocabularySource,
      });
      setRevised(result.revised);
      setWordCount(result.vocabulary_word_count ?? null);
      setWordsUsed((result.vocabulary_words_used ?? []) as WordWithCardId[]);
      if (revised) setFollowUpPrompt('');
    } catch (err: any) {
      const status = err?.response?.status;
      if (status === 404) setError('Text not found.');
      else if (status === 503) setError('AI is unavailable right now. Please try again later.');
      else setError('Revision failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleReplace = async () => {
    if (!revised || !onReplace) return;
    setReplacing(true);
    setError(null);
    try {
      await onReplace(revised, wordsUsed);
      setReplaced(true);
    } catch {
      setError('Could not save the revision. Please try again.');
    } finally {
      setReplacing(false);
    }
  };

  return (
    <>
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
        <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl mx-4 flex flex-col max-h-[90vh]">
          <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 flex-shrink-0">
            <h2 className="text-base font-semibold text-gray-800">Revise Text</h2>
            <button
              onClick={onClose}
              className="p-1.5 text-gray-400 hover:text-gray-600 rounded-full transition-colors"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          <div className="overflow-y-auto flex-1 px-5 py-4 space-y-4">
            <div>
              <p className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-1.5">Your text</p>
              <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap bg-gray-50 rounded-lg p-3 border border-gray-100">
                {storyText}
              </p>
            </div>

            {!revised && (
              <>
                <div>
                  <p className="text-xs font-medium text-gray-500 mb-2">Vocabulary</p>
                  <div className="inline-flex rounded-lg border border-gray-200 p-0.5 bg-gray-50">
                    <button
                      type="button"
                      onClick={() => setVocabularySource('free')}
                      className={`px-3 py-1.5 text-xs rounded-md font-medium transition-colors ${
                        vocabularySource === 'free'
                          ? 'bg-white text-gray-800 shadow-sm'
                          : 'text-gray-500 hover:text-gray-700'
                      }`}
                    >
                      Fresh revision
                    </button>
                    <button
                      type="button"
                      onClick={() => setVocabularySource('library')}
                      className={`px-3 py-1.5 text-xs rounded-md font-medium transition-colors ${
                        vocabularySource === 'library'
                          ? 'bg-white text-gray-800 shadow-sm'
                          : 'text-gray-500 hover:text-gray-700'
                      }`}
                    >
                      Use my cards
                    </button>
                  </div>
                  <p className="mt-1.5 text-xs text-gray-400">
                    {vocabularySource === 'library'
                      ? 'The AI will incorporate vocabulary from your saved cards.'
                      : 'The AI will revise freely without referencing your cards.'}
                  </p>
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1.5">
                    How would you like it revised?{' '}
                    <span className="text-gray-400 font-normal">(optional — leave blank for a native-speaker revision)</span>
                  </label>
                  <textarea
                    value={prompt}
                    onChange={(e) => setPrompt(e.target.value)}
                    placeholder="e.g. make it more conversational, simplify the vocabulary…"
                    rows={3}
                    className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 resize-none focus:outline-none focus:ring-2 focus:ring-indigo-300"
                  />
                </div>
              </>
            )}

            {error && <p className="text-sm text-red-600">{error}</p>}

            {revised && (
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <div className="flex items-center gap-2">
                    <p className="text-xs font-medium text-indigo-500 uppercase tracking-wide">Revised version</p>
                    {wordCount != null && wordCount > 0 && (
                      <span className="text-xs text-gray-400">{wordCount} cards from your library used</span>
                    )}
                  </div>
                  {onReplace && (
                    <button
                      onClick={handleReplace}
                      disabled={replacing || replaced}
                      className="flex items-center gap-1 text-xs text-indigo-600 hover:text-indigo-800 disabled:opacity-50 transition-colors"
                    >
                      {replacing
                        ? <Loader2 className="h-3 w-3 animate-spin" />
                        : replaced
                          ? <Check className="h-3 w-3" />
                          : null
                      }
                      {replaced ? 'Saved' : replacing ? 'Saving…' : 'Use this version'}
                    </button>
                  )}
                </div>
                <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap bg-indigo-50 rounded-lg p-3 border border-indigo-100">
                  {highlightWordsWithLinks(revised, wordsUsed, setOpenCardId)}
                </p>
                {wordsUsed.length > 0 && (
                  <p className="text-xs text-gray-400 mt-1">Tap a highlighted word to open its card.</p>
                )}
              </div>
            )}

            {revised && (
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1.5">
                  Refine further{' '}
                  <span className="text-gray-400 font-normal">(optional)</span>
                </label>
                <textarea
                  value={followUpPrompt}
                  onChange={(e) => setFollowUpPrompt(e.target.value)}
                  placeholder="e.g. make it shorter, use more formal language…"
                  rows={2}
                  className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 resize-none focus:outline-none focus:ring-2 focus:ring-indigo-300"
                />
              </div>
            )}
          </div>

          <div className="px-5 py-4 border-t border-gray-100 flex justify-end gap-2 flex-shrink-0">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 transition-colors"
            >
              Close
            </button>
            <button
              onClick={handleRevise}
              disabled={loading}
              className="flex items-center gap-1.5 px-4 py-2 bg-indigo-600 text-white text-sm rounded-lg hover:bg-indigo-700 disabled:opacity-50 transition-colors"
            >
              {loading
                ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                : revised
                  ? <RefreshCw className="h-3.5 w-3.5" />
                  : <Sparkles className="h-3.5 w-3.5" />
              }
              {revised ? 'Revise again' : 'Revise'}
            </button>
          </div>
        </div>
      </div>

      {openCardId && (
        <CardDetail
          cardId={openCardId}
          onClose={() => setOpenCardId(null)}
          onSave={() => {}}
        />
      )}
    </>
  );
}
